import axios from 'axios';
import React from 'react';
import { useSelector } from 'react-redux';
import { appInfoSelector, getUser } from 'Selectors';
import Iframe from 'Components/Iframe';
import {
    BULK_UPLOAD_SEND_CHUNK,
    BULK_UPLOAD_BEGIN_CHUNKING,
    BULK_UPLOAD_CHUNKING_COMPLETE,
    BULK_UPLOAD_FILE_DELETED,
    BULK_UPLOAD_GET_NEXT_CHUNK,
    BULK_UPLOAD_PROCESSING_BEGIN,
    BULK_UPLOAD_PROCESSING_COMPLETE,
    BULK_UPLOAD_VALID_FILE,
    GET_TOKEN_ERROR,
    LOADED
} from 'Utils';
import { Loading } from '@brighterion/br-ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useBulkListEntry from 'Hooks/query-hooks/listEntry/useBulkListEntry';
import { useHistory, useLocation } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { ListEntryBulkObject } from '@brighterion/types/listEntry';
import { getISOStringFromString } from '@brighterion/utils/date-timezone';
import {
    beginChunking,
    deleteFile,
    externalLoad,
    POSSIBLE_STATUS,
    toggleConfirmModal,
    updateCurrentChunkNumber,
    updateProcessingCompleteFileDetails,
    updateStatus,
    updateValidFileDetails,
    useUploaderContext,
    useUploaderDispatchContext
} from '../Uploader/context';
import styles from './PCIFileUploader.css';
import useBulkListChunkingComplete from '../../hooks/useBulkListChunkingCompleteAddSuccessModal';
import useListTypeBulkUploaderColumns from '../../hooks/useListTypeBulkUploaderColumns';
import useBulkListServerError from '../../hooks/useBulkListServerError';

const PCIFileUploader = React.forwardRef((_props, ref: React.Ref<HTMLIFrameElement>) => {
    const refCurrent = (ref as React.RefObject<HTMLIFrameElement>)?.current;
    const { customerId } = useSelector(getUser);
    const appInfo = useSelector(appInfoSelector);
    const dispatch = useUploaderDispatchContext();
    const { t } = useTranslation();
    const { listId, fileDetails, listName, merchantSpecific, listType, customListType, status } = useUploaderContext();
    const mutation = useBulkListEntry({ listId });
    const { addModal, clearAll } = useReduxModal();
    const history = useHistory();
    const location = useLocation();
    const successfulBulk = useBulkListChunkingComplete();
    const errorBulking = useBulkListServerError();
    const columns = useListTypeBulkUploaderColumns(listType, merchantSpecific, customListType);

    const isLoaded = status === POSSIBLE_STATUS.loaded;
    const isLoading = status === POSSIBLE_STATUS.loading;
    const isLoadingError = status === POSSIBLE_STATUS.errorLoading;
    const isChunking = status === POSSIBLE_STATUS.chunking || status === POSSIBLE_STATUS.chunkingComplete;

    // message handler
    React.useEffect(() => {
        const listener = (e: MessageEvent) => {
            // do this to get origin
            const { origin } = new URL(appInfo!.pciComponentsURL);
            if (e.origin !== origin) return;
            if (e?.data) {
                const pData = e?.data;
                // check the name so that you are listening to this pci components events
                // if you omit this check, you'll get all INPUT_LOADED events for any
                // PCI component that fires this event.
                if (pData?.name === 'pci-bulk') {
                    const { payload = {}, type } = pData ?? {};
                    switch (type) {
                        case LOADED:
                            dispatch(externalLoad({ ...pData.payload }));
                            break;
                        case BULK_UPLOAD_PROCESSING_BEGIN: {
                            dispatch(updateStatus(POSSIBLE_STATUS.processingFile));
                            break;
                        }
                        case BULK_UPLOAD_VALID_FILE: {
                            dispatch(updateValidFileDetails({ ...pData.payload }));
                            break;
                        }
                        case BULK_UPLOAD_PROCESSING_COMPLETE: {
                            dispatch(
                                updateProcessingCompleteFileDetails({
                                    ...pData.payload
                                })
                            );
                            dispatch(toggleConfirmModal({ value: true, changeStatus: false }));
                            break;
                        }
                        case BULK_UPLOAD_BEGIN_CHUNKING: {
                            // informs us that the iframe heard our request to chunk
                            // update the status to chunking
                            dispatch(beginChunking());
                            break;
                        }
                        case BULK_UPLOAD_SEND_CHUNK: {
                            // this is where you'd want to make the axios post
                            // to the rules api; on success, get the next chunk
                            const chunkedCSV = payload.data;
                            // token-service doesn't validate startTime and endTime,
                            // so ui has to validate it
                            chunkedCSV?.forEach((row: ListEntryBulkObject) => {
                                if (row.startTime) {
                                    row.startTime = getISOStringFromString(row.startTime);
                                }
                                if (row.endTime) {
                                    row.endTime = getISOStringFromString(row.endTime);
                                }
                            });

                            mutation.mutate(chunkedCSV, {
                                onSuccess: () => {
                                    dispatch(updateCurrentChunkNumber(payload?.currentChunkNumber));
                                    refCurrent?.contentWindow?.postMessage(
                                        {
                                            type: BULK_UPLOAD_GET_NEXT_CHUNK
                                        },
                                        refCurrent?.src
                                    );
                                },
                                onError: errorBulking
                            });
                            break;
                        }
                        case BULK_UPLOAD_CHUNKING_COMPLETE: {
                            dispatch(updateStatus(POSSIBLE_STATUS.chunkingComplete));
                            successfulBulk();
                            break;
                        }
                        case BULK_UPLOAD_FILE_DELETED: {
                            dispatch(deleteFile());
                            break;
                        }
                        case GET_TOKEN_ERROR: {
                            errorBulking();
                            break;
                        }
                        default:
                            break;
                    }
                }
            }
        };

        window.addEventListener('message', listener);

        return () => {
            window.removeEventListener('message', listener);
        };
    }, [
        addModal,
        appInfo,
        dispatch,
        fileDetails.totalNumberOfRows,
        history,
        listName,
        location.pathname,
        mutation,
        refCurrent,
        t,
        clearAll,
        successfulBulk,
        errorBulking
    ]);

    // you need to fire off an init message once loaded
    // to provided the jwt
    React.useEffect(() => {
        if (isLoaded) {
            const payload: { jwt: any; columns: BulkUploaderColumn[]; tenantId: string } = {
                jwt: axios.defaults.headers.common.Authorization,
                tenantId: customerId,
                columns
            };
            refCurrent?.contentWindow?.postMessage(
                {
                    type: 'INIT',
                    payload
                },
                refCurrent?.src
            );
        }
    }, [refCurrent, isLoaded, columns, customerId]);

    React.useEffect(() => {
        let timer: NodeJS.Timeout;
        // no reliable way to tell if iframe has failed to load
        // so if we have not received the LOADED message within 60 seconds, we can assume
        // that there is an issue with the tokenization server.
        if (isLoading) {
            timer = setTimeout(() => {
                dispatch(updateStatus(POSSIBLE_STATUS.errorLoading));
            }, 60000);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [dispatch, isLoading]);

    return (
        <div className={clsx(styles.pciFileUploaderContainer, isChunking && styles.chunking)}>
            {isLoadingError && (
                <div className={styles.pciSiblingContainer}>
                    <p>{t('pciBulkLoadError')}</p>
                </div>
            )}
            {isLoading && (
                <div className={styles.pciSiblingContainer}>
                    <Loading size='md' />
                </div>
            )}
            <Iframe
                ref={ref}
                className={clsx(styles.iframe, isLoading && styles.loading, isLoadingError && styles.loadingError)}
                name='pci-bulk'
                src={`${appInfo!.pciComponentsURL}/tokenize_bulk`}
            />
        </div>
    );
});

export default PCIFileUploader;
